import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { showError } from '../utils/notifications';
import { 
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import API_CONFIG from '../config';

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Définir fetchProducts AVANT de l'utiliser dans useEffect
  const fetchProducts = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/users/products`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        throw new Error('Erreur lors de la récupération des produits');
      }

      const productsData = await response.json();
      // Trier par ordre alphabétique
      const sortedProducts = productsData.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
      setProducts(sortedProducts);
    } catch (error) {
      console.error('Erreur:', error);
      showError('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchProducts();
  }, [navigate, fetchProducts]);





  // Filtrer les produits par recherche uniquement
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Grouper les produits par première lettre
  const groupedProducts = filteredProducts.reduce((groups, product) => {
    const firstLetter = product.name.charAt(0).toUpperCase();
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(product);
    return groups;
  }, {});

  // Obtenir toutes les lettres disponibles (non utilisé mais gardé pour référence)
  // const availableLetters = Object.keys(groupedProducts).sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des produits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Mes Produits</h1>

          </div>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              className="w-full pl-9 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
            />
          </div>
        </div>
      </div>

      {/* Liste des produits - conteneur scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        <div className="px-3 sm:px-4 lg:px-6 pt-4 sm:pt-6 pb-32 sm:pb-6">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            {searchTerm ? (
              <div>
                <p className="text-gray-500 mb-4">Aucun produit trouvé pour "{searchTerm}"</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-primary hover:text-primary-dark"
                >
                  Effacer la recherche
                </button>
              </div>
            ) : (
              <div>
                <p className="text-gray-500 mb-4">Vous n'avez pas encore de produits</p>
                <p className="text-gray-500">Utilisez le bouton + en bas de l'écran pour ajouter un produit</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {Object.keys(groupedProducts).sort().map((letter) => (
              <div key={letter}>
                <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3 px-2">
                  {letter}
                </h2>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {groupedProducts[letter].map((product, index) => (
                    <div
                      key={product.id}
                      className={`flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                        index !== groupedProducts[letter].length - 1 ? 'border-b border-gray-100' : ''
                      }`}
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-medium text-gray-900 truncate">
                          {product.name}
                        </h3>
                        {product.unit_price && (
                          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                            {product.unit_price.toFixed(2)}€
                            {product.unit_type && ` / ${product.unit_type}`}
                          </p>
                        )}
                      </div>
                      

                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default Products; 